import { chatDrawerActions } from 'ducks/chatDrawer'
import { isActionWhitelisted, requiresConfirmation } from 'layout/widgets/adaChat/actionPermissions'
import {
  BatteryDesignAssistantArgs,
  runBatteryDesignAssistantCore,
} from 'layout/widgets/adaChat/utils/runBatteryDesignAssistantCore'
import { osFetch } from 'util/fetch'
import {
  isInDesignTab,
  validateActionRequirementsOrThrow,
} from 'layout/widgets/adaChat/utils/validateActionRequirements'
import { LiveFunctionCall } from '../GeminiLiveAPI/lib/multimodal-live-types'

interface RunBatteryDesignAssistantArgs extends BatteryDesignAssistantArgs {
  confirmed?: boolean
}

export const TOOL_NAME = 'run_battery_design_assistant'

export async function RunBatteryDesignAssistantToolCall(
  functionCall: LiveFunctionCall,
  history: any,
  orgId: number | undefined
) {
  const args = functionCall.args as RunBatteryDesignAssistantArgs

  console.log('[Voice] RunBatteryDesignAssistantToolCall', args)

  if (!orgId) {
    throw new Error('Organization ID is required to run battery design assistant')
  }

  const pathname = window.location.pathname
  validateActionRequirementsOrThrow('runBatteryDesignAssistant', pathname)

  // Extract project ID from pathname
  const { projectId } = isInDesignTab(pathname)

  if (!projectId) {
    throw new Error('Project ID is required to run battery design assistant')
  }

  // Check if this action requires confirmation
  const isWhitelisted = isActionWhitelisted('runBatteryDesignAssistant')

  // If user provided all required parameters, treat as implicit confirmation
  const hasAllRequiredParams =
    args.customer_goal !== undefined &&
    args.backup_hours !== undefined &&
    args.continuous_power_kw !== undefined &&
    args.peak_power_kw !== undefined

  if (requiresConfirmation('runBatteryDesignAssistant') && !isWhitelisted) {
    if (!args.confirmed && !hasAllRequiredParams) {
      const errorMessage =
        'This action requires confirmation. Please ask the user: "Would you like me to get battery recommendations for this system?" and wait for their confirmation before proceeding.'
      console.error('[Voice] Battery design assistant requires confirmation but confirmed flag not set:', errorMessage)
      throw new Error(errorMessage)
    }
    // If all required params are provided, automatically treat as confirmed
    if (!args.confirmed && hasAllRequiredParams) {
      args.confirmed = true
    }
  }

  // Get the selected system
  const system = window.editor?.selectedSystem
  if (!system) {
    throw new Error('No system selected. Please select a system in the design.')
  }

  console.log('[Voice] Starting runBatteryDesignAssistant for system:', system.uuid)

  // Create a message with toolExecutionState
  if (window.reduxStore && projectId) {
    const state = window.reduxStore.getState()
    const messages = state?.chatDrawer?.messages || []
    const lastMessageIndex = messages.length > 0 ? messages.length - 1 : -1

    if (lastMessageIndex >= 0) {
      window.reduxStore.dispatch(
        chatDrawerActions.updateToolExecutionState(lastMessageIndex, {
          toolType: 'batteryDesignAssistant',
          status: 'running',
          projectId: projectId,
          isAutoAccepted: isWhitelisted,
        })
      )
    } else {
      window.reduxStore.dispatch(chatDrawerActions.addMessage('Getting battery recommendations...', 'receive', 'voice'))
      const newState = window.reduxStore.getState()
      const newMessages = newState?.chatDrawer?.messages || []
      const newLastMessageIndex = newMessages.length > 0 ? newMessages.length - 1 : -1
      if (newLastMessageIndex >= 0) {
        window.reduxStore.dispatch(
          chatDrawerActions.updateToolExecutionState(newLastMessageIndex, {
            toolType: 'batteryDesignAssistant',
            status: 'running',
            projectId: projectId,
            isAutoAccepted: isWhitelisted,
          })
        )
      }
    }
  }

  // Call the API to get battery recommendations
  if (!window.reduxStore) {
    throw new Error('Redux store not available')
  }

  try {
    // Use shared core logic
    const result = await runBatteryDesignAssistantCore(args, system.uuid, projectId, async (postData) => {
      const response = await osFetch(
        `${window.API_ROOT}/api/orgs/${orgId}/projects/${projectId}/systems/${system.uuid}/recommend_battery_sets/`,
        {
          method: 'POST',
          headers: window.Utils.tokenAuthHeaders({
            'X-CSRFToken': window.getCookie('csrftoken'),
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(postData),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `API request failed with status ${response.status}`)
      }

      const responseData = await response.json()

      if (!responseData || !Array.isArray(responseData)) {
        throw new Error('No battery recommendations returned from the server.')
      }

      return responseData
    })

    // Update the message to show ready state
    if (window.reduxStore) {
      const state = window.reduxStore.getState()
      const messages = state?.chatDrawer?.messages || []

      let targetMessageIndex = -1
      for (let i = messages.length - 1; i >= 0; i--) {
        if (
          messages[i].direction === 'receive' &&
          messages[i].toolExecutionState &&
          messages[i].toolExecutionState.toolType === 'batteryDesignAssistant'
        ) {
          targetMessageIndex = i
          break
        }
      }

      if (targetMessageIndex >= 0) {
        window.reduxStore.dispatch(
          chatDrawerActions.updateToolExecutionState(targetMessageIndex, {
            toolType: 'batteryDesignAssistant',
            status: 'ready',
            description: result.description,
            data: {
              batterySets: result.batterySets,
              customerPriority: result.customerPriority,
              essentialBackupMetrics: result.essentialBackupMetrics,
            },
            projectId: projectId,
          })
        )
        console.log('[Voice] Battery design assistant completed, recommendations:', result.batterySets.length)
      }
    }

    // Format recommendations for voice reading
    const formatBatteryRecommendations = (batterySets: any[]): string => {
      if (!batterySets || batterySets.length === 0) {
        return result.description
      }

      const recommendations = batterySets.slice(0, 3).map((batterySet: any, index: number) => {
        const firstBattery = batterySet.batteries?.[0]
        const batteryName = firstBattery?.name || firstBattery?.code || `Option ${index + 1}`
        const manufacturer = firstBattery?.manufacturer_name || firstBattery?.manufacturer
        const totalCapacity = batterySet.total_usable_capacity || 0
        const quantity = batterySet.total_battery_quantity || 0
        const price = batterySet.price
        const gridIndependence = batterySet.grid_independence
        const selfConsumption = batterySet.self_consumption

        let description = `Option ${index + 1}: ${manufacturer ? `${manufacturer} ` : ''}${batteryName}`
        if (totalCapacity > 0) {
          description += `, ${totalCapacity.toFixed(1)} kilowatt hours total capacity`
        }
        if (quantity > 0) {
          description += `, quantity of ${quantity}`
        }
        if (price !== undefined && price !== null) {
          description += `, price ${price.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`
        }
        if (gridIndependence !== undefined && gridIndependence !== null) {
          description += `, ${gridIndependence.toFixed(1)} percent grid independence`
        }
        if (selfConsumption !== undefined && selfConsumption !== null) {
          description += `, ${selfConsumption.toFixed(1)} percent self consumption`
        }

        return description
      })

      return `Found ${batterySets.length} battery recommendation${
        batterySets.length > 1 ? 's' : ''
      }. ${recommendations.join(
        '. '
      )}. To load a recommendation, say "load option 1" for the first option, "load option 2" for the second option, or "load option 3" for the third option.`
    }

    return {
      success: true,
      message: formatBatteryRecommendations(result.batterySets),
    }
  } catch (error: any) {
    console.error('[Voice] Battery design assistant error:', error)

    // Update the toolExecutionState to 'error'
    if (window.reduxStore && projectId) {
      const state = window.reduxStore.getState()
      const messages = state?.chatDrawer?.messages || []

      let targetMessageIndex = -1
      for (let i = messages.length - 1; i >= 0; i--) {
        if (
          messages[i].direction === 'receive' &&
          messages[i].toolExecutionState &&
          messages[i].toolExecutionState.toolType === 'batteryDesignAssistant'
        ) {
          targetMessageIndex = i
          break
        }
      }

      if (targetMessageIndex >= 0) {
        window.reduxStore.dispatch(
          chatDrawerActions.updateToolExecutionState(targetMessageIndex, {
            toolType: 'batteryDesignAssistant',
            status: 'error',
            projectId: projectId,
            description: error?.message || 'Battery design assistant failed',
            error: {
              message: error?.message || 'Battery design assistant failed',
            },
          })
        )
      }
    }

    throw error
  }
}
